import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import { ExecutionModalV2 } from '@/components/execution/ExecutionModalV2';
import {
  Play,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  Ban,
  Search,
  Filter,
  Download,
  Server,
  ChevronDown,
  ChevronUp,
  Calendar,
  User,
  Zap,
  X,
  RefreshCw,
  CalendarClock,
  Power,
  PowerOff,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Eye,
  Edit,
  Square,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { executionsApi, schedulesApi, workflowsApi, batchJobsApi } from '@/lib/api';
import { format, formatDistanceToNow } from 'date-fns';
import type { Execution, ExecutionStatus } from '@shared/types/execution';
import type { WorkflowSchedule } from '@/types/schedule';
import { parseCronExpression, getTimeUntilRun } from '@/utils/cronParser';
import { useViewportPageSize } from '@/hooks/use-viewport-page-size';
import { ExecutionsPageSkeleton } from '@/components/ui/page-skeletons';

interface Workflow {
  id: string;
  name: string;
}

type SortField = 'started_at' | 'completed_at' | 'status';
type SortDirection = 'asc' | 'desc';

const statusConfig = {
  running: { icon: Loader2, label: 'Running', color: 'text-blue-500', bgColor: 'bg-blue-500/10', iconClass: 'animate-spin' },
  success: { icon: CheckCircle2, label: 'Success', color: 'text-green-500', bgColor: 'bg-green-500/10', iconClass: '' },
  failed: { icon: XCircle, label: 'Failed', color: 'text-red-500', bgColor: 'bg-red-500/10', iconClass: '' },
  waiting: { icon: Clock, label: 'Waiting', color: 'text-yellow-500', bgColor: 'bg-yellow-500/10', iconClass: '' },
  cancelled: { icon: Ban, label: 'Cancelled', color: 'text-gray-500', bgColor: 'bg-gray-500/10', iconClass: '' },
};

const triggerTypeConfig = {
  manual: { icon: User, label: 'Manual', color: 'text-blue-500' },
  schedule: { icon: Clock, label: 'Schedule', color: 'text-purple-500' },
  webhook: { icon: Zap, label: 'Webhook', color: 'text-orange-500' },
  mcp: { icon: Server, label: 'MCP', color: 'text-green-500' },
};

export default function Executions() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [schedules, setSchedules] = useState<WorkflowSchedule[]>([]);
  const [batchJobs, setBatchJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingSchedules, setLoadingSchedules] = useState(true);
  const [loadingBatchJobs, setLoadingBatchJobs] = useState(false);
  const [selectedExecution, setSelectedExecution] = useState<Execution | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'executions' | 'scheduled' | 'batchJobs'>('executions');

  // Server-side pagination with dynamic page size based on viewport height
  const pageSize = useViewportPageSize({
    rowHeight: 57,                        // Table row height (tuned to maximize rows without scrollbar)
    minPageSize: 5,                       // Lower minimum for smaller screens
    maxPageSize: 50,                      // Reasonable maximum to avoid performance issues
    bufferSpace: 45,                      // 40px header row + 5px safety margin
    loading,                              // Recalculate when loading completes
  });

  // Get page from URL query params (needed early for pagination state init)
  const pageFromUrl = searchParams.get('page');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // Track if we've initialized from URL (to prevent other effects from overriding)
  const hasInitializedFromUrl = useRef(false);

  // Sync page from URL when navigating back to the list
  useEffect(() => {
    if (pageFromUrl) {
      const page = parseInt(pageFromUrl, 10);
      if (!isNaN(page) && page > 0) {
        setCurrentPage(page);
        hasInitializedFromUrl.current = true;
      }
    } else {
      // No page param means we're not returning from editor, reset the flag
      hasInitializedFromUrl.current = false;
    }
  }, [pageFromUrl]);

  const [totalExecutions, setTotalExecutions] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [workflowFilter, setWorkflowFilter] = useState<string>('');
  const [triggerFilter, setTriggerFilter] = useState<string>('');

  // Sorting
  const [sortField, setSortField] = useState<SortField>('completed_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Debounce search input - only reset page when user actually types a search
  const debouncedSetSearch = useMemo(
    () => debounce((value: string) => {
      setDebouncedSearchQuery(value);
      // Only reset to page 1 when user is actively searching (non-empty query)
      if (value !== '') {
        setCurrentPage(1);
      }
    }, 300),
    []
  );

  useEffect(() => {
    debouncedSetSearch(searchQuery);
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [searchQuery, debouncedSetSearch]);

  // Load data when filters, sorting, or pagination changes
  useEffect(() => {
    if (activeTab === 'executions') {
      loadExecutions();
    }
  }, [currentPage, pageSize, statusFilter, workflowFilter, triggerFilter, sortField, sortDirection, debouncedSearchQuery, activeTab]);

  useEffect(() => {
    if (activeTab === 'scheduled') {
      loadSchedules();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'batchJobs') {
      loadBatchJobs();
    }
  }, [activeTab]);

  useEffect(() => {
    loadWorkflows();
  }, []);

  // Poll for updates when there are running executions
  useEffect(() => {
    const hasRunningExecutions = executions.some(
      exec => exec.status === 'running' || exec.status === 'waiting'
    );

    if (!hasRunningExecutions || activeTab !== 'executions') {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const response = await executionsApi.list({
          limit: pageSize,
          offset: (currentPage - 1) * pageSize,
          status: statusFilter || undefined,
          workflowId: workflowFilter || undefined,
          triggerType: triggerFilter || undefined,
          search: debouncedSearchQuery || undefined,
          sortBy: sortField,
          sortOrder: sortDirection,
        });

        // Only update if data has actually changed
        setExecutions(prevExecutions => {
          const rawExecutions = response.data.executions;
          const transformedExecutions = (rawExecutions || []).map((exec: any) => ({
            id: exec.id,
            workflowId: exec.workflow_id,
            workflowName: exec.workflow_name || exec.workflow_id,
            userId: exec.user_id || 'unknown',
            status: exec.status,
            startedAt: exec.started_at || exec.created_at,
            completedAt: exec.completed_at,
            triggerType: exec.trigger_type || 'manual',
            error: exec.error,
            executionData: {
              startTime: new Date(exec.started_at || exec.created_at),
              logs: [],
              nodeResults: {},
              tier: 'sandboxed' as const,
            },
          }));
          const hasChanges = transformedExecutions.some((newExec: any, index: number) => {
            const oldExec = prevExecutions[index];
            return !oldExec || 
                   oldExec.status !== newExec.status || 
                   oldExec.completedAt !== newExec.completedAt;
          });
          return hasChanges ? transformedExecutions : prevExecutions;
        });
        
        setTotalExecutions(response.data.total);
      } catch (error) {
        // Silent fail for background polling
        // console.error('Background polling error:', error);
      }
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [executions, activeTab, currentPage, pageSize, statusFilter, workflowFilter, triggerFilter, debouncedSearchQuery, sortField, sortDirection]);

  // Force re-render every 15 seconds to update relative time displays ("in 45 seconds")
  const [, setTick] = useState(0);
  useEffect(() => {
    if (activeTab !== 'scheduled') {
      return;
    }

    const tickInterval = setInterval(() => {
      setTick(t => t + 1);
    }, 15000); // Update relative times every 15 seconds

    return () => clearInterval(tickInterval);
  }, [activeTab]);

  // Poll for schedule updates
  useEffect(() => {
    if (activeTab !== 'scheduled') {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const response = await schedulesApi.list();
        
        // Fetch workflow details for each schedule to get active status
        const schedulesWithWorkflowData = await Promise.all(
          (response.data || []).map(async (schedule: any) => {
            try {
              const workflowResponse = await workflowsApi.get(schedule.workflowId);
              return {
                ...schedule,
                enabled: schedule.enabled === 1 || schedule.enabled === true,
                workflowActive: workflowResponse.data?.active || false,
              };
            } catch (error) {
              return {
                ...schedule,
                enabled: schedule.enabled === 1 || schedule.enabled === true,
                workflowActive: false,
              };
            }
          })
        );
        
        // Only update if data has actually changed
        setSchedules(prevSchedules => {
          const hasChanges = schedulesWithWorkflowData.some((newSched: any, index: number) => {
            const oldSched = prevSchedules[index];
            return !oldSched || 
                   oldSched.nextRunAt !== newSched.nextRunAt || 
                   oldSched.lastRunAt !== newSched.lastRunAt ||
                   oldSched.workflowActive !== newSched.workflowActive;
          });
          
          return hasChanges ? schedulesWithWorkflowData : prevSchedules;
        });
      } catch (error) {
        // Silent fail for background polling
        // console.error('Background polling error:', error);
      }
    }, 10000); // Every 10 seconds

    return () => clearInterval(pollInterval);
  }, [activeTab]);

  const loadExecutions = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }

      const params: any = {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        sortBy: sortField,
        sortOrder: sortDirection,
      };

      if (statusFilter) params.status = statusFilter;
      if (workflowFilter) params.workflowId = workflowFilter;
      if (triggerFilter) params.triggerType = triggerFilter;
      if (debouncedSearchQuery) params.search = debouncedSearchQuery;

      const response = await executionsApi.list(params);
      const { executions: executionsData, total, hasMore: more } = response.data;
      

      const transformedExecutions = (executionsData || []).map((exec: any) => ({
        id: exec.id,
        workflowId: exec.workflow_id,
        workflowName: exec.workflow_name || exec.workflow_id,
        userId: exec.user_id || 'unknown',
        status: exec.status,
        startedAt: exec.started_at || exec.created_at,
        completedAt: exec.completed_at,
        triggerType: exec.trigger_type || 'manual',
        error: exec.error,
        executionData: {
          startTime: new Date(exec.started_at || exec.created_at),
          logs: [],
          nodeResults: {},
          tier: 'sandboxed' as const,
        },
      }));

      setExecutions(transformedExecutions);
      setTotalExecutions(total);
      setHasMore(more);
    } catch (error) {
      if (!silent) {
        toast.error('Failed to load executions');
      }
      // console.error('Failed to load executions:', error);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const loadWorkflows = async () => {
    try {
      // Use endpoint that only returns workflows with executions that user can see
      const response = await executionsApi.getWorkflowsWithExecutions();
      const workflowsData = Array.isArray(response.data) ? response.data : [];
      setWorkflows(workflowsData);
    } catch (error) {
      // console.error('Failed to load workflows:', error);
    }
  };

  const loadBatchJobs = async () => {
    try {
      setLoadingBatchJobs(true);
      const response = await batchJobsApi.list();
      setBatchJobs(response.data?.batchJobs || []);
    } catch (error) {
      toast.error('Failed to load batch jobs');
    } finally {
      setLoadingBatchJobs(false);
    }
  };

  const loadSchedules = async () => {
    try {
      setLoadingSchedules(true);
      const response = await schedulesApi.list();
      
      // Fetch workflow details for each schedule to get active status
      const schedulesWithWorkflowData = await Promise.all(
        (response.data || []).map(async (schedule: any) => {
          try {
            const workflowResponse = await workflowsApi.get(schedule.workflowId);
            return {
              ...schedule,
              enabled: schedule.enabled === 1 || schedule.enabled === true,
              workflowActive: workflowResponse.data?.active || false,
            };
          } catch (error) {
            // console.error(`Failed to fetch workflow ${schedule.workflowId}:`, error);
            return {
              ...schedule,
              enabled: schedule.enabled === 1 || schedule.enabled === true,
              workflowActive: false,
            };
          }
        })
      );
      
      setSchedules(schedulesWithWorkflowData);
    } catch (error) {
      // console.error('Failed to load schedules:', error);
      toast.error('Failed to load schedules');
    } finally {
      setLoadingSchedules(false);
    }
  };

  const handleToggleWorkflow = async (workflowId: string, currentActive: boolean) => {
    try {
      const response = await workflowsApi.toggleActive(workflowId);
      const newActive = response.data?.active ?? !currentActive;
      toast.success(newActive ? 'Workflow activated' : 'Workflow deactivated');
      loadSchedules();
    } catch (error) {
      toast.error('Failed to toggle workflow');
    }
  };

  const handleRefreshSchedules = async () => {
    try {
      setLoadingSchedules(true);
      const response = await schedulesApi.refresh();
      const { recalculated, schedules: refreshedSchedules } = response.data;
      
      if (recalculated > 0) {
        toast.success(`Refreshed ${recalculated} overdue schedule${recalculated > 1 ? 's' : ''}`);
      } else {
        toast.info('All schedules are up to date');
      }
      
      // Reload schedules to get the updated data
      await loadSchedules();
    } catch (error) {
      toast.error('Failed to refresh schedules');
      // console.error('Failed to refresh schedules:', error);
    } finally {
      setLoadingSchedules(false);
    }
  };



  const workflowNameMap = useMemo(() => {
    const map = new Map<string, string>();
    workflows.forEach(w => map.set(w.id, w.name));
    return map;
  }, [workflows]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1); // Reset to first page
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setWorkflowFilter('');
    setTriggerFilter('');
    setCurrentPage(1);
  };

  const hasActiveFilters = searchQuery || statusFilter || workflowFilter || triggerFilter;

  const handleCancelExecution = async (executionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await executionsApi.cancel(executionId);
      toast.success('Execution cancellation requested');
      // Optimistically update the UI to show cancelled status
      setExecutions(prev => prev.map(exec =>
        exec.id === executionId
          ? { ...exec, status: 'cancelled' as const }
          : exec
      ));
      // Refresh after a delay to get the actual status from backend
      setTimeout(() => loadExecutions(), 1000);
    } catch (error) {
      toast.error('Failed to cancel execution');
    }
  };

  const totalPages = Math.ceil(totalExecutions / pageSize);

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />;
  };

  const formatDuration = (startedAt: string | null, completedAt: string | null) => {
    if (!startedAt) return '-';
    const start = new Date(startedAt);
    const end = completedAt ? new Date(completedAt) : new Date();
    const durationMs = end.getTime() - start.getTime();
    
    if (durationMs < 1000) return `${durationMs}ms`;
    if (durationMs < 60000) return `${(durationMs / 1000).toFixed(1)}s`;
    if (durationMs < 3600000) return `${Math.floor(durationMs / 60000)}m ${Math.floor((durationMs % 60000) / 1000)}s`;
    return `${Math.floor(durationMs / 3600000)}h ${Math.floor((durationMs % 3600000) / 60000)}m`;
  };

  const exportToCSV = () => {
    const headers = ['ID', 'Workflow', 'Status', 'Trigger', 'Started', 'Completed', 'Duration'];
    const rows = executions.map(exec => [
      exec.id,
      exec.workflowName || workflowNameMap.get(exec.workflowId) || exec.workflowId,
      exec.status,
      exec.triggerType,
      exec.startedAt ? format(new Date(exec.startedAt), 'yyyy-MM-dd HH:mm:ss') : '',
      exec.completedAt ? format(new Date(exec.completedAt), 'yyyy-MM-dd HH:mm:ss') : '',
      formatDuration(exec.startedAt, exec.completedAt),
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `executions-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <ExecutionsPageSkeleton />;
  }

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden p-4 md:p-8 pt-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Executions</h2>
          <p className="text-muted-foreground">
            View and manage workflow execution history
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="flex-1 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="executions" className="relative">
              Executions
              {totalExecutions > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {totalExecutions}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="scheduled" className="relative">
              Scheduled
              {schedules.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {schedules.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="batchJobs" className="relative">
              Batch Jobs
            </TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-2">
            {activeTab === 'executions' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadExecutions()}
                  disabled={loading}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button variant="outline" size="sm" onClick={exportToCSV} disabled={executions.length === 0}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </>
            )}
            {activeTab === 'scheduled' && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRefreshSchedules} 
                disabled={loadingSchedules}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loadingSchedules ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            )}
          </div>
        </div>

        <TabsContent value="executions" className="flex-1 flex flex-col overflow-hidden space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-[300px]">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by workflow name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>

            <Select value={statusFilter || "all"} onValueChange={(value) => setStatusFilter(value === "all" ? "" : value)}>
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="running">Running</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="waiting">Waiting</SelectItem>
              </SelectContent>
            </Select>

            <Select value={workflowFilter || "all"} onValueChange={(value) => setWorkflowFilter(value === "all" ? "" : value)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All workflows" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All workflows</SelectItem>
                {workflows.map(workflow => (
                  <SelectItem key={workflow.id} value={workflow.id}>
                    {workflow.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={triggerFilter || "all"} onValueChange={(value) => setTriggerFilter(value === "all" ? "" : value)}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All triggers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All triggers</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="schedule">Schedule</SelectItem>
                <SelectItem value="webhook">Webhook</SelectItem>
                <SelectItem value="mcp">MCP</SelectItem>
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                <X className="h-4 w-4 mr-2" />
                Clear
              </Button>
            )}
          </div>

          {/* Executions Table */}
          <div data-table-container className="flex-1 overflow-auto">
            <Card>
              <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : executions.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-center">
                  <Play className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No executions found</h3>
                  <p className="text-sm text-muted-foreground">
                    {hasActiveFilters ? 'Try adjusting your filters' : 'Execute a workflow to see results here'}
                  </p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[100px]">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSort('status')}
                            className="h-8 px-2"
                          >
                            Status
                            <SortIcon field="status" />
                          </Button>
                        </TableHead>
                        <TableHead>Workflow</TableHead>
                        <TableHead>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSort('started_at')}
                            className="h-8 px-2"
                          >
                            Started
                            <SortIcon field="started_at" />
                          </Button>
                        </TableHead>
                        <TableHead>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSort('completed_at')}
                            className="h-8 px-2"
                          >
                            Completed
                            <SortIcon field="completed_at" />
                          </Button>
                        </TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Trigger</TableHead>
                        <TableHead className="w-[100px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {executions.map((execution) => {
                        const statusInfo = statusConfig[execution.status as keyof typeof statusConfig];
                        const StatusIcon = statusInfo?.icon || Clock;
                        const triggerInfo = triggerTypeConfig[execution.triggerType as keyof typeof triggerTypeConfig];
                        const TriggerIcon = triggerInfo?.icon || User;

                        return (
                          <TableRow
                            key={execution.id}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => {
                              setSelectedExecution(execution);
                              setDetailsOpen(true);
                            }}
                          >
                            <TableCell>
                              <Badge variant="outline" className={`${statusInfo?.bgColor} ${statusInfo?.color}`}>
                                <StatusIcon className={`h-3 w-3 mr-1 ${statusInfo?.iconClass}`} />
                                {statusInfo?.label}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-medium">
                              {execution.workflowName || workflowNameMap.get(execution.workflowId) || execution.workflowId}
                            </TableCell>
                            <TableCell>
                              {execution.startedAt ? (
                                <div className="flex flex-col">
                                  <span className="text-sm">
                                    {format(new Date(execution.startedAt), 'MMM d, yyyy HH:mm')}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {formatDistanceToNow(new Date(execution.startedAt), { addSuffix: true })}
                                  </span>
                                </div>
                              ) : (
                                '-'
                              )}
                            </TableCell>
                            <TableCell>
                              {execution.completedAt ? (
                                <div className="flex flex-col">
                                  <span className="text-sm">
                                    {format(new Date(execution.completedAt), 'MMM d, yyyy HH:mm')}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {formatDistanceToNow(new Date(execution.completedAt), { addSuffix: true })}
                                  </span>
                                </div>
                              ) : (
                                '-'
                              )}
                            </TableCell>
                            <TableCell>
                              <span className="font-mono text-sm">
                                {formatDuration(execution.startedAt, execution.completedAt)}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <TriggerIcon className={`h-4 w-4 ${triggerInfo?.color}`} />
                                <span className="text-sm">{triggerInfo?.label}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedExecution(execution);
                                    setDetailsOpen(true);
                                  }}
                                  title="View details"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const pageParam = currentPage > 1 ? `&page=${currentPage}` : '';
                                    navigate(`/workflows/${execution.workflowId}?returnTo=executions${pageParam}`);
                                  }}
                                  title="Edit workflow"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                {(execution.status === 'running' || execution.status === 'waiting') && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                                    onClick={(e) => handleCancelExecution(execution.id, e)}
                                    title="Cancel execution"
                                  >
                                    <Square className="h-4 w-4 fill-current" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </>
              )}
            </CardContent>
          </Card>
        </div>

          {/* Pagination */}
          {!loading && executions.length > 0 && (
            <div data-pagination-footer className="flex-shrink-0 flex flex-col sm:flex-row items-center justify-between gap-2 px-3 py-1.5 bg-card border rounded-lg mt-2">
              <div className="text-sm text-muted-foreground text-center sm:text-left">
                Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalExecutions)} of {totalExecutions} executions
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">Prev</span>
                </Button>
                <div className="flex items-center gap-1">
                  <span className="text-sm font-medium whitespace-nowrap">
                    Page {currentPage} of {totalPages}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={!hasMore}
                >
                  <span className="hidden sm:inline">Next</span>
                  <span className="sm:hidden">Next</span>
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="scheduled" className="space-y-4">
          <Card>
            <CardContent className="p-0">
              {loadingSchedules ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : schedules.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-center">
                  <CalendarClock className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No scheduled workflows</h3>
                  <p className="text-sm text-muted-foreground">
                    Create a schedule to automate workflow execution
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Workflow</TableHead>
                      <TableHead>Schedule</TableHead>
                      <TableHead>Next Run</TableHead>
                      <TableHead>Last Run</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[150px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {schedules.map((schedule) => {
                      const workflowName = schedule.workflowName || workflowNameMap.get(schedule.workflowId) || schedule.workflowId;
                      const cronReadable = parseCronExpression(schedule.cronExpression);
                      const timeUntil = schedule.nextRunAt ? getTimeUntilRun(schedule.nextRunAt) : null;
                      
                      // Check if schedule is overdue
                      const now = Math.floor(Date.now() / 1000);
                      const nextRunTimestamp = typeof schedule.nextRunAt === 'number' 
                        ? schedule.nextRunAt 
                        : schedule.nextRunAt ? Math.floor(new Date(schedule.nextRunAt).getTime() / 1000) : null;
                      const isOverdue = nextRunTimestamp && nextRunTimestamp < now;

                      return (
                        <TableRow key={schedule.id}>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium">{workflowName}</span>
                              {schedule.description && (
                                <span className="text-xs text-muted-foreground">{schedule.description}</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="text-sm">{cronReadable}</span>
                              <span className="text-xs text-muted-foreground font-mono">{schedule.cronExpression}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {schedule.nextRunAt ? (
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                  <span className={`text-sm ${!schedule.workflowActive ? 'line-through text-muted-foreground' : ''}`}>
                                    {format(
                                      typeof schedule.nextRunAt === 'number' 
                                        ? new Date(schedule.nextRunAt * 1000) 
                                        : new Date(schedule.nextRunAt), 
                                      'MMM d, yyyy HH:mm'
                                    )}
                                  </span>
                                  {isOverdue && schedule.workflowActive && (
                                    <Badge variant="outline" className="bg-orange-500/10 text-orange-500 text-xs">
                                      Overdue
                                    </Badge>
                                  )}
                                </div>
                                {!schedule.workflowActive ? (
                                  <span className="text-xs text-muted-foreground">
                                    Won't run - workflow inactive
                                  </span>
                                ) : timeUntil && !isOverdue ? (
                                  <span className="text-xs text-muted-foreground">{timeUntil}</span>
                                ) : isOverdue ? (
                                  <span className="text-xs text-orange-500">
                                    Will run at next cron interval
                                  </span>
                                ) : null}
                              </div>
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {schedule.lastRunAt ? (
                              <div className="flex flex-col gap-1">
                                <span className="text-sm">
                                  {format(
                                    typeof schedule.lastRunAt === 'number' 
                                      ? new Date(schedule.lastRunAt * 1000) 
                                      : new Date(schedule.lastRunAt), 
                                    'MMM d, yyyy HH:mm'
                                  )}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {formatDistanceToNow(
                                    typeof schedule.lastRunAt === 'number' 
                                      ? new Date(schedule.lastRunAt * 1000) 
                                      : new Date(schedule.lastRunAt), 
                                    { addSuffix: true }
                                  )}
                                </span>
                              </div>
                            ) : (
                              <span className="text-sm text-muted-foreground">Never</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <Badge
                                variant="outline"
                                className={schedule.workflowActive ? 'bg-green-500/10 text-green-500' : 'bg-gray-500/10 text-gray-500'}
                              >
                                {schedule.workflowActive ? 'Active' : 'Inactive'}
                              </Badge>
                              {!schedule.workflowActive && (
                                <span className="text-xs text-muted-foreground">
                                  Schedule paused
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant={schedule.workflowActive ? "outline" : "default"}
                              size="sm"
                              onClick={() => handleToggleWorkflow(schedule.workflowId, schedule.workflowActive)}
                              className="w-28"
                            >
                              {schedule.workflowActive ? (
                                <>
                                  <PowerOff className="h-4 w-4 mr-2" />
                                  Deactivate
                                </>
                              ) : (
                                <>
                                  <Power className="h-4 w-4 mr-2" />
                                  Activate
                                </>
                              )}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="batchJobs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Batch Jobs</CardTitle>
              <CardDescription>
                Track AI batch processing jobs submitted via the AI Batch node. Jobs are automatically polled for completion.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {loadingBatchJobs ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : batchJobs.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-center">
                  <Server className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No batch jobs yet</h3>
                  <p className="text-sm text-muted-foreground">
                    Batch jobs will appear here when you use the AI Batch node in your workflows.
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead>Workflow</TableHead>
                      <TableHead>Node</TableHead>
                      <TableHead>Provider</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead>Completed</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {batchJobs.map((job) => {
                      const statusColors: Record<string, string> = {
                        Pending: 'bg-gray-500/10 text-gray-500',
                        Submitted: 'bg-blue-500/10 text-blue-500',
                        InProgress: 'bg-blue-500/10 text-blue-500',
                        Completed: 'bg-green-500/10 text-green-500',
                        PartialFailure: 'bg-yellow-500/10 text-yellow-500',
                        Failed: 'bg-red-500/10 text-red-500',
                        Expired: 'bg-orange-500/10 text-orange-500',
                        Cancelled: 'bg-gray-500/10 text-gray-500',
                      };

                      return (
                        <TableRow key={job.id}>
                          <TableCell>
                            <Badge variant="outline" className={statusColors[job.status] || 'bg-gray-500/10'}>
                              {(job.status === 'Submitted' || job.status === 'InProgress') && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                              {job.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium">
                            {job.workflowName || job.workflowId}
                          </TableCell>
                          <TableCell>{job.nodeName}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{job.provider}</Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm font-mono">
                              {job.totalRequests}
                              {job.failedRequests > 0 && (
                                <span className="text-red-500 ml-1">({job.failedRequests} failed)</span>
                              )}
                            </span>
                          </TableCell>
                          <TableCell>
                            {job.submittedAt ? (
                              <div className="flex flex-col">
                                <span className="text-sm">
                                  {format(new Date(job.submittedAt), 'MMM d, HH:mm')}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {formatDistanceToNow(new Date(job.submittedAt), { addSuffix: true })}
                                </span>
                              </div>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            {job.completedAt ? (
                              <div className="flex flex-col">
                                <span className="text-sm">
                                  {format(new Date(job.completedAt), 'MMM d, HH:mm')}
                                </span>
                                {job.submittedAt && (
                                  <span className="text-xs text-muted-foreground">
                                    {(() => {
                                      const durationMs = new Date(job.completedAt).getTime() - new Date(job.submittedAt).getTime();
                                      const minutes = Math.floor(durationMs / 60000);
                                      const seconds = Math.floor((durationMs % 60000) / 1000);
                                      if (minutes > 0) return `${minutes}m ${seconds}s`;
                                      return `${seconds}s`;
                                    })()}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {(job.status === 'Submitted' || job.status === 'InProgress') && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={async () => {
                                      try {
                                        await batchJobsApi.forcePoll(job.id);
                                        toast.success('Force poll requested');
                                        loadBatchJobs();
                                      } catch (error) {
                                        toast.error('Failed to force poll');
                                      }
                                    }}
                                    title="Force poll for status update"
                                  >
                                    <RefreshCw className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-500 hover:text-red-600"
                                    onClick={async () => {
                                      try {
                                        await batchJobsApi.cancel(job.id);
                                        toast.success('Batch job cancelled');
                                        loadBatchJobs();
                                      } catch (error) {
                                        toast.error('Failed to cancel batch job');
                                      }
                                    }}
                                    title="Cancel batch job"
                                  >
                                    <Ban className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Execution Details Modal - Lazy loads node data when opened */}
      {selectedExecution && detailsOpen && (
        <ExecutionModalV2
          execution={{
            id: selectedExecution.id,
            status: selectedExecution.status as 'completed' | 'failed' | 'running' | 'cancelled',
            started_at: selectedExecution.startedAt ? new Date(selectedExecution.startedAt).getTime() : Date.now(),
            completed_at: selectedExecution.completedAt ? new Date(selectedExecution.completedAt).getTime() : undefined,
            duration_ms: selectedExecution.completedAt && selectedExecution.startedAt
              ? new Date(selectedExecution.completedAt).getTime() - new Date(selectedExecution.startedAt).getTime()
              : undefined,
            workflow_name: selectedExecution.workflowName || workflowNameMap.get(selectedExecution.workflowId) || selectedExecution.workflowId,
            error: selectedExecution.error,
          }}
          nodes={[]} // Empty array - modal will lazy load node executions
          onClose={() => setDetailsOpen(false)}
        />
      )}
    </div>
  );
}
